// M&A system (M3) — docs/ARCHITECTURE_M3.md §1, design plan §3 Stage 5 and
// locked decision #5 (the bundle model). Both directions:
//  - BUYING a rival delivers a bundle — team, synthetic products carrying their
//    share, sometimes a star — plus an integration-risk window. Paying in STOCK
//    issues shares at the market price: an overvalued stock is cheap currency,
//    which is what finally makes guidance credibility score-relevant.
//  - BEING BOUGHT arrives as an approach-ask; accepting ends the campaign as
//    deathReason "acquired" with liquidation preferences binding (1× non-
//    participating for private companies; clean pro-rata once public).

import type { ContentPack, Product, RivalState } from "../types.js";
import { DISCIPLINES, stageAtLeast } from "../types.js";
import type { TickContext } from "../ctx.js";
import { charge, emit } from "../ctx.js";
import { interpolate } from "../calendar.js";
import { expectedNodeIndex, getNode } from "../content.js";
import { dieArea } from "../formulas/yieldModel.js";
import { expectedKGates } from "./market.js";
import { rivalDef, rivalDefOf, rivalStrengthIn } from "./rivals.js";
import { refreshFounderShare, totalShares, ttmEarnings } from "./finance.js";
import { moraleHit } from "./talent.js";
import { openSystemAsk } from "./stakeholders.js";
import { clamp } from "../prng.js";

type Rivals = Record<string, RivalState>;

/** A rival's implied revenue run-rate from its market presences (pricing basis).
 *  Pure in (content, rivals) — bots and the UI price deals from PlayerView. */
export function rivalRevenueProxy(content: ContentPack, rivals: Rivals, id: string, m: number): number {
  const def = rivalDefOf(content, rivals, id);
  const st = rivals[id]!;
  const models = content.economy.models;
  let annual = 0;
  for (const presence of def.markets) {
    const market = content.markets.find((mk) => mk.id === presence.marketId);
    if (!market) continue;
    const strength = rivalStrengthIn(def, st, presence.marketId, m);
    if (strength <= 0) continue;
    const share = Math.min(models.rivalShareCap, models.rivalShareCoeff * strength);
    annual += share * interpolate(market.tamUnitsPerMonth, m) * interpolate(market.refAsp, m) * 12;
  }
  return annual;
}

export function rivalIsDistressed(content: ContentPack, rivals: Rivals, id: string, m: number): boolean {
  const def = rivalDefOf(content, rivals, id);
  const st = rivals[id]!;
  const imploding = def.fateWindow !== undefined && m >= def.fateWindow.from && m <= def.fateWindow.to;
  return st.scale < 0.55 || imploding;
}

export function rivalAcquirable(content: ContentPack, rivals: Rivals, id: string, m: number): boolean {
  const def = rivalDefOf(content, rivals, id);
  const st = rivals[id]!;
  if (!st.alive || def.foundedMonth > m) return false;
  if (def.acquirableFrom === undefined) return false;
  return m >= def.acquirableFrom;
}

/** Sticker price for a rival: P/S on its revenue proxy, premium or distress discount. */
export function rivalPrice(content: ContentPack, rivals: Rivals, id: string, m: number): number {
  const eco = content.economy;
  const ps = interpolate(eco.street.psMultiple, m);
  const mult = rivalIsDistressed(content, rivals, id, m) ? eco.ma.distressedDiscount : eco.ma.rivalPremium;
  // even a shell has a team and a patent drawer: floor on era capital
  const floor = 2_000_000 * interpolate(eco.eraCapitalScale, m);
  return Math.round(Math.max(floor, ps * rivalRevenueProxy(content, rivals, id, m) * mult));
}

/** The acquisition bundle (locked decision #5). Returns a rejection reason or null. */
export function acquireRival(ctx: TickContext, rivalId: string, payment: "cash" | "stock"): string | null {
  const { s, content } = ctx;
  const eco = content.economy;
  const ma = eco.ma;
  const st = s.rivals[rivalId];
  if (!st) return "unknown rival";
  if (!st.alive) return "that company no longer exists";
  if (!rivalAcquirable(content, s.rivals, rivalId, s.monthIndex)) return "they are not for sale";
  if (!stageAtLeast(s.stage, ma.minStageToBuy)) return "acquisitions need a real balance sheet (growth stage)";
  const def = rivalDef(ctx, rivalId);
  const price = rivalPrice(content, s.rivals, rivalId, s.monthIndex);
  const m = s.monthIndex;

  if (payment === "cash") {
    if (s.cash < price) return `the deal is $${Math.round(price / 1e6)}M — you don't have it in cash`;
    charge(ctx, "acquisition", -price, `acquired ${def.displayName} for cash`);
  } else {
    if (s.stage !== "public" || !s.street) return "stock deals need a public currency";
    const shares = Math.round(price / Math.max(0.01, s.street.sharePrice));
    s.finance.publicShares += shares; // paper issued at the MARKET price — rich paper is cheap money
    refreshFounderShare(ctx);
  }

  // the bundle: team (instant, they already work together)…
  for (const d of DISCIPLINES) {
    const heads = Math.round(ma.scaleTeamSize[d] * st.scale);
    if (heads > 0) s.people.pools[d].productive += heads;
  }
  // …their sockets, as synthetic products that keep selling on the existing pipeline…
  let stumbled = false;
  if (ctx.rng.bernoulli(ma.stumbleChance)) stumbled = true;
  for (const presence of def.markets) {
    const market = content.markets.find((mk) => mk.id === presence.marketId);
    if (!market) continue;
    const strength = rivalStrengthIn(def, st, presence.marketId, m);
    if (strength <= 0) continue;
    const models = eco.models;
    const share =
      Math.min(models.rivalShareCap, models.rivalShareCoeff * strength) * (stumbled ? ma.stumbleShareFactor : 1);
    const releasedMonth = Math.max(0, m - st.cyclePos);
    const kGates = Math.max(5, Math.round(0.8 * expectedKGates(market, releasedMonth)));
    const node = content.nodes[expectedNodeIndex(content, m, market.leadingEdgeLagMonths)]!;
    const product: Product = {
      id: `acq-${rivalId}-${presence.marketId}`,
      projectId: `acq-${rivalId}`,
      marketId: presence.marketId,
      nodeId: node.id,
      releasedMonth,
      qualityExec: clamp(0.72 + 0.12 * Math.min(1.5, strength), 0.6, 1.0),
      designWinRoll: 1,
      competitivenessAtRelease: 0,
      share,
      kGates,
      dieAreaMm2: dieArea(kGates, getNode(content, node.id), eco.models),
      active: true,
      channelUnits: 0,
      lastAsp: null,
      royaltyPct: 0,
      acquired: true,
      // acquired share carries at the market's neutral baseline (archFit = 1);
      // buying a rival's TREE position (signature-node credits) is P2 work
      archNodes: [],
    };
    s.products.push(product);
  }
  // …sometimes their star…
  if (ctx.rng.bernoulli(ma.starChance * st.scale)) {
    const td = eco.talent;
    const name = td.starNames[(s.counters.nextStar - 1) % td.starNames.length]!;
    const discipline = def.path === "analog" ? "design" : "implementation";
    const effHeads = def.path === "analog" ? td.starEffHeadsAnalog : td.starEffHeadsDigital;
    // addStar via stakeholder effects would be circular; inline the essentials
    s.talent.stars.push({
      id: `star-${s.counters.nextStar}`,
      name,
      discipline,
      effHeads,
      salaryMult: td.starSalaryMult,
      joinedMonth: m,
    });
    s.counters.nextStar += 1;
  }
  // …and the integration window: churn, morale, and maybe a stumble
  s.talent.integrationMonthsLeft = Math.max(s.talent.integrationMonthsLeft, eco.talent.integrationMonths);
  moraleHit(ctx, -eco.talent.integrationMoraleHit);
  st.alive = false;
  st.diedMonth = m;
  st.acquiredByPlayer = true;

  const bd = eco.board.deltas["rivalAcquired"];
  if (bd !== undefined) s.board.confidence = clamp(s.board.confidence + bd, 0, 100);
  if (stumbled) {
    const sd = eco.board.deltas["integrationStumble"];
    if (sd !== undefined) s.board.confidence = clamp(s.board.confidence + sd, 0, 100);
    emit(ctx, "integrationStumble", `${def.displayName}'s key engineers walked during the transition — the sockets came over damaged.`, {
      rivalId,
    });
  }

  const pools = content.rivals.newsPools;
  const template = pools.acquired.length > 0 ? pools.acquired[(m + rivalId.length) % pools.acquired.length]! : "{name} acquired.";
  emit(
    ctx,
    "rivalAcquired",
    template.replace(/\{name\}/g, def.displayName).replace(/\{market\}/g, def.markets[0]?.marketId ?? "the industry"),
    { rivalId, price, payment, stumbled },
  );
  return null;
}

/** End the campaign as an exit. Liquidation preferences (deferred from M2) bind
 *  here: private investors take max(1× on invested capital, pro-rata) first;
 *  public caps are clean pro-rata. Score = the founder's actual take. */
export function executeExit(ctx: TickContext, price: number): void {
  const { s } = ctx;
  const fin = s.finance;
  let founderTake: number;
  if (s.stage === "public") {
    founderTake = price * fin.founderShare;
  } else {
    const total = totalShares(fin);
    const proRata = price * (fin.investorShares / total);
    const investorTake = Math.min(price, Math.max(fin.totalRaised, proRata));
    const commonShares = total - fin.investorShares;
    founderTake = commonShares > 0 ? (price - investorTake) * (fin.founderShares / commonShares) : 0;
  }
  fin.exitFounderTake = Math.max(0, Math.round(founderTake));
  s.dead = true;
  s.deathMonth = s.monthIndex;
  s.deathReason = "acquired";
  emit(
    ctx,
    "companyAcquired",
    `The company is acquired for $${(price / 1e6).toFixed(0)}M. Founder take after the waterfall: $${(fin.exitFounderTake / 1e6).toFixed(1)}M.`,
    { price, founderTake: fin.exitFounderTake },
  );
}

/** Close-phase: acquisition approaches (design §12 tier 2, both directions). */
export function processMaClose(ctx: TickContext): void {
  const { s, content } = ctx;
  if (s.dead) return;
  const ma = content.economy.ma;
  const m = s.monthIndex;

  if (s.ma.approachCooldownMonths > 0) {
    s.ma.approachCooldownMonths = Math.max(0, s.ma.approachCooldownMonths - ctx.monthsPerTick);
    return;
  }
  const ttm = s.finance.ttmRevenue.reduce((a, b) => a + b, 0);
  if (ttm < ma.approachMinTtm) return;

  let price = 0;
  if (s.stage === "public" && s.street) {
    const cap = s.street.sharePrice * totalShares(s.finance);
    if (cap < ma.cheapTtmMult * ttm && ctx.rng.bernoulli(ma.approachHazardPublicCheap * ctx.monthsPerTick)) {
      price = Math.round(cap * (1 + ma.publicPremium));
    }
  } else if (stageAtLeast(s.stage, "growth")) {
    const sentiment = interpolate(content.economy.sentiment, m);
    if (sentiment >= ma.hotSentimentFloor && ctx.rng.bernoulli(ma.approachHazardPrivate * ctx.monthsPerTick)) {
      const st = content.economy.street;
      const story = Math.max(
        interpolate(st.psMultiple, m) * ttm,
        interpolate(st.peMultiple, m) * Math.max(0, ttmEarnings(s.kpiHistory)),
      );
      price = Math.round(Math.max(s.finance.lastPostMoney, story) * ma.privatePremium);
    }
  }
  if (price <= 0) return;

  s.ma.approachCooldownMonths = ma.approachCooldownMonths;
  openSystemAsk(ctx, {
    source: "rival",
    headline: "An acquirer comes calling",
    body:
      `A strategic buyer wants the company — team, sockets, roadmap — for $${(price / 1e6).toFixed(0)}M. ` +
      (s.stage === "public"
        ? `That's a premium to a tape that hasn't been kind to you lately. The board will hear about this either way.`
        : `Your investors' preferences come off the top; what's left is yours. Selling now prices the future you'd otherwise have to build.`),
    footnote:
      "Most fabless startups exited by acquisition, not IPO — and the sale price depended less on revenue than on WHEN in the cycle the buyer asked.",
    monthsLeft: content.economy.ma.approachExpiresMonths,
    defaultOption: 1,
    options: [
      { label: "Sell the company", effects: { sellCompany: { price } } },
      { label: "We're not done building", effects: {} },
    ],
  });
  emit(ctx, "acquisitionOffer", `Acquisition approach: $${(price / 1e6).toFixed(0)}M on the table.`, { price });
}
